/**
 * The owner's language and area unit: what everything a BUYER sees is written in
 * (tour.json `locale`, the floor-plan caption, /t, /p and /r), and how areas read.
 *
 * Stored on the tour doc by the app when a capture starts (TourDoc.language /
 * TourDoc.areaUnit). Tours from before 2026-10-28 have neither and read as Spanish +
 * m², which is what they were published as. Areas are always STORED in m² (the
 * contract is metric); square feet is a display unit only.
 *
 * Pure helpers, no Intl: the app (Hermes) and node:test must print the same thing.
 */
export const TOUR_LANGUAGES = ['es', 'en'];
/**
 * The app's UI languages, as recorded on analytics docs (onboardingFunnel, captureFunnel,
 * clientEvents). Wider than TOUR_LANGUAGES: a French UI still publishes es/en tours.
 * MIRRORS LOCALES in apps/mobile/src/i18n/i18n.ts and the `locale in [...]` checks in
 * firestore.rules; deploy the rules before an app build that sends a new one.
 */
export const APP_LOCALES = ['es', 'en', 'fr', 'de', 'it', 'pt'];
export const AREA_UNITS = ['m2', 'sqft'];
/** Square feet in one square metre (1 ft = 0.3048 m exactly). */
export const SQFT_PER_M2 = 1 / (0.3048 * 0.3048);
/** A stored tour language, or Spanish for old tours and anything unreadable. */
export function tourLanguage(value) {
    return value === 'en' ? 'en' : 'es';
}
/** A stored area unit, or m² for old tours and anything unreadable. */
export function areaUnit(value) {
    return value === 'sqft' ? 'sqft' : 'm2';
}
/**
 * The area unit each country's property listings quote, by ISO 3166 alpha-2 region
 * (founder decision 2026-10-28: "use the measurement system each country uses").
 * Only regions that need saying are listed; every other country gets m². Explicit m²
 * entries are countries someone might guess wrong. The app lets the user override this
 * in Ajustes → Unidades. Stored areas stay m² whatever the unit (the contract is metric).
 *
 * MIRRORED in apps/mobile/src/i18n/market.ts (AREA_UNIT_BY_REGION); the mobile test
 * reads this file and fails if the two tables differ. Edit both together.
 */
const AREA_UNIT_BY_REGION = {
    US: 'sqft', // Zillow, Realtor.com and the MLSs quote square feet only
    CA: 'sqft', // metric on paper, but REALTOR.ca and agents list square feet
    GB: 'sqft', // Rightmove/Zoopla floor plans lead with sq ft (m² alongside)
    IE: 'm2', // Daft.ie and BER certificates use m², unlike the UK next door
    IN: 'sqft', // carpet / built-up area is sold per sq ft (99acres, MagicBricks)
    PK: 'sqft', // flats are listed in sq ft (Zameen); houses in marla, which we can't show
    BD: 'sqft', // Dhaka flats are priced and listed per sq ft
    HK: 'sqft', // saleable area in sq ft, by law and on every portal
    SG: 'sqft', // PropertyGuru and HDB resale listings quote sq ft and $ psf
    MY: 'sqft', // iProperty / PropertyGuru Malaysia quote sq ft
    AE: 'sqft', // Bayut and Property Finder UAE quote sq ft
    QA: 'm2', // Property Finder Qatar lists sqm, unlike the UAE
    LR: 'sqft', // US customary units in everyday use
    MM: 'sqft', // imperial units in everyday use; listings in sq ft
};
export function areaUnitForRegion(region) {
    return (region !== undefined && AREA_UNIT_BY_REGION[region.toUpperCase()]) || 'm2';
}
export function isCurrencyCode(value) {
    return typeof value === 'string' && /^[A-Z]{3}$/.test(value);
}
/** An m² figure in `unit`, rounded to a whole number (areas are approximate anyway). */
export function areaIn(m2, unit) {
    return Math.round(unit === 'sqft' ? m2 * SQFT_PER_M2 : m2);
}
/** es-ES groups with '.' and (RAE) leaves 4-digit figures alone; English uses ',' from 1,000. */
function group(n, lang) {
    const digits = String(Math.abs(n));
    const sign = n < 0 ? '-' : '';
    if (lang === 'es' && digits.length <= 4)
        return sign + digits;
    return sign + digits.replace(/\B(?=(\d{3})+(?!\d))/g, lang === 'es' ? '.' : ',');
}
const UNIT_LABEL = {
    m2: { es: 'm²', en: 'm²' },
    sqft: { es: 'ft²', en: 'sq ft' },
};
/** `85 m²`, `915 sq ft`, `1,076 sq ft`, `1076 ft²`. */
export function formatArea(m2, unit, lang) {
    return `${group(areaIn(m2, unit), lang)} ${UNIT_LABEL[unit][lang]}`;
}
/** Metres in one inch (exact). */
const METRES_PER_INCH = 0.0254;
const INCHES_PER_FOOT = 12;
/**
 * A length for a plan dimension: `3,46 m` / `3.46 m` for m² owners, `11' 6"` (nearest
 * inch) for sq ft owners, the way their listings print room sizes.
 */
export function formatLength(metres, unit, lang) {
    if (unit === 'sqft') {
        const inches = Math.round(metres / METRES_PER_INCH);
        return `${Math.floor(inches / INCHES_PER_FOOT)}' ${inches % INCHES_PER_FOOT}"`;
    }
    const fixed = metres.toFixed(2);
    return `${lang === 'es' ? fixed.replace('.', ',') : fixed} m`;
}
//# sourceMappingURL=locale.js.map